/**
 * Media URL Resolver — SINGLE SOURCE OF TRUTH
 *
 * All image/video URL resolution must go through this module.
 * Never duplicate resolveMedia logic in page or component files.
 *
 * Rules:
 *   1. Absolute https/http URLs → use as-is
 *   2. /api/ relative URLs → prepend API_URL
 *   3. Empty / null / non-string → return ""
 */

import { API_URL } from "./apiConfig";

/**
 * Resolves a media URL to an absolute URL.
 * @param {string} url - The media URL to resolve
 * @returns {string} The resolved absolute URL
 */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string" || url.trim() === "") return "";
  if (url.startsWith("https://") || url.startsWith("http://")) return url;
  if (url.startsWith("/api/")) return `${API_URL}${url}`;
  return url;
}

/** Alias kept for backwards compatibility during migration */
export const resolveMedia = resolveMediaUrl;

/**
 * Gets the best available media URL for a product.
 * Prioritizes pos_media_url over image_url.
 * 
 * @param {Object} product - Product object with image_url and/or pos_media_url
 * @returns {string} The resolved media URL or empty string
 */
export function getProductMediaUrl(product) {
  if (!product) return "";
  const url = product.pos_media_url || product.image_url;
  return resolveMediaUrl(url);
}

/**
 * Gets the category thumbnail URL.
 * Uses category's own image_url or falls back to first product's image.
 * 
 * @param {Object} category - Category object with products array
 * @returns {string} The resolved thumbnail URL or empty string
 */
export function getCategoryThumbnailUrl(category) {
  if (!category) return "";
  
  // Use category's own image if available
  if (category.image_url) {
    return resolveMediaUrl(category.image_url);
  }
  
  // Fallback to first product with an image
  const products = category.products || category.items || [];
  const productWithImage = products.find(p => p.pos_media_url || p.image_url);
  if (productWithImage) {
    return getProductMediaUrl(productWithImage);
  }
  
  return "";
}

export default {
  resolveMediaUrl,
  getProductMediaUrl,
  getCategoryThumbnailUrl
};
